import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TimelineItemWithCommentCount } from "@travel/shared";
import { api } from "../../api/client";
import { BottomSheet } from "../common/BottomSheet";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface Props {
  item: TimelineItemWithCommentCount | null;
  planId: string;
  onClose: () => void;
  onEdit: (item: TimelineItemWithCommentCount) => void;
  onComment: (item: TimelineItemWithCommentCount) => void;
}

export function ItemActionSheet({ item, planId, onClose, onEdit, onComment }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const qc = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan", planId] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    },
  });

  if (!item) return null;
  const hasMap = !!item.googleMapLink;

  return (
    <>
      <BottomSheet open={!!item && !confirmOpen} onClose={onClose} title={item.title}>
        <ul className="divide-y divide-slate-100">
          <ActionRow onClick={() => onEdit(item)}>수정하기</ActionRow>
          <ActionRow onClick={() => onComment(item)}>
            코멘트 남기기 {item.commentCount > 0 && `(${item.commentCount})`}
          </ActionRow>
          <ActionRow
            onClick={() => {
              if (item.googleMapLink) {
                window.open(item.googleMapLink, "_blank", "noopener,noreferrer");
                onClose();
              }
            }}
            disabled={!hasMap}
          >
            Google Map 보기
          </ActionRow>
          <ActionRow onClick={() => setConfirmOpen(true)} danger>
            삭제하기
          </ActionRow>
        </ul>
        <div className="h-2" />
      </BottomSheet>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deleteMutation.mutate(item.id)}
        title="이 일정을 삭제하시겠어요?"
        message={item.title}
        confirmLabel="삭제"
        destructive
      />
    </>
  );
}

interface RowProps {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

function ActionRow({ children, onClick, danger, disabled }: RowProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`w-full text-left px-4 py-3.5 min-h-[48px] active:bg-slate-100 disabled:opacity-40 disabled:pointer-events-none ${
          danger ? "text-rose-600" : "text-slate-900"
        }`}
      >
        {children}
      </button>
    </li>
  );
}
